import { supabase } from '@/lib/supabase';
import { PropBot } from '@/types/prop-bots';

export interface ReassignmentOptions {
  isReversePosition: boolean;
  keepOnCurrentPage: boolean;
  disconnectDuringChange?: boolean;
  volumeMultiplier?: number;
}

export interface ReassignmentResult {
  success: boolean;
  error?: string;
  account_id?: string;
  old_bot_id?: string;
  old_original_bot_id?: string;
  new_bot_id?: string;
  new_original_bot_id?: string;
  is_reverse_position?: boolean;
  keep_on_current_page?: boolean;
  signal_token?: string;
}

// Fetch available prop bots for reassignment (excluding the current bot)
export const fetchAvailablePropBots = async (excludeBotId?: string): Promise<PropBot[]> => {
  const query = supabase
    .from('bots')
    .select('*')
    .eq('type', 'prop_bot')
    .eq('status', 'active')
    .neq('is_deleted', true);
    
  if (excludeBotId) {
    query.neq('id', excludeBotId);
  }
    
  const { data, error } = await query.order('name', { ascending: true });
  
  if (error) throw error;
  return (data || []) as PropBot[];
};

// Call the Supabase RPC function to reassign a bot account
export const reassignBotAccount = async (
  accountId: string,
  targetOriginalBotId: string,
  options: ReassignmentOptions
): Promise<ReassignmentResult> => {
  // Build the RPC parameters, only including volume_multiplier if it's provided
  const rpcParams: any = {
    p_account_id: accountId,
    p_target_original_bot_id: targetOriginalBotId,
    p_is_reverse_position: options.isReversePosition,
    p_keep_on_current_page: options.keepOnCurrentPage,
  };
  
  // Only include volume_multiplier if it's defined
  if (options.volumeMultiplier !== undefined) {
    rpcParams.p_volume_multiplier = options.volumeMultiplier;
  }
  
  const { data, error } = await supabase.rpc('reassign_bot_account', rpcParams);
  
  if (error) throw error;
  return data as ReassignmentResult;
};

// Optional: Disconnect account before reassignment
export const disconnectAccountTemporarily = async (accountId: string) => {
  const { error } = await supabase
    .from('bot_accounts')
    .update({ status: 'disconnected' })
    .eq('id', accountId);
    
  if (error) throw error;
  return { success: true };
};

// Optional: Reconnect account after reassignment
export const reconnectAccount = async (accountId: string) => {
  const { error } = await supabase
    .from('bot_accounts')
    .update({ status: 'connected' })
    .eq('id', accountId);
    
  if (error) throw error;
  return { success: true };
};